'use client';

/**
 * 朗读音频的客户端缓存：同一段文字重复朗读时不再打后端。
 *
 * 与 src/lib/providers/tts/cache.ts 不是重复实现——那份是服务端 GCS 共享缓存
 * （跨用户复用，省的是供应商调用费），这份是浏览器本地缓存（省的是网络往返）。
 * 服务端那份依赖 node crypto 与 @google-cloud/storage，不能引入客户端包，所以
 * 两边各有一份存取实现；**失效口径**则统一由服务端定义，见下面的 identity。
 *
 * Web 端优先用 Cache Storage（持久、跨刷新有效）；在不可用的环境
 * （非安全上下文、Safari 无痕、SSR）退化成内存 Map，行为一致只是不跨刷新。
 */

/**
 * 存储格式版本。只在本文件的存取结构变化时才递增（比如换 key 拼法、换存的
 * 载体）；「合成参数变了要作废旧音频」由下面的 synthesis identity 负责，
 * 两件事分开，别混用同一个开关。
 */
const CACHE_NAME = 'alfagpt-tts-v2';
const STALE_CACHE_NAMES = ['alfagpt-tts-v1'];
/** Cache Storage 需要一个合法 URL 作为 key，用这个虚构 origin 拼 */
const CACHE_KEY_ORIGIN = 'https://tts.alfagpt.local/';
/** 内存兜底缓存的条目上限，避免长会话堆积 */
const MEMORY_CACHE_LIMIT = 60;

const memoryCache = new Map<string, Blob>();

function isCacheStorageAvailable(): boolean {
  return typeof window !== 'undefined' && typeof window.caches !== 'undefined';
}

/** 32 位 FNV-1a，subtle.digest 不可用时的同步兜底 */
function fnv1a(input: string): string {
  let hash = 0x811c9dc5;
  for (let i = 0; i < input.length; i++) {
    hash ^= input.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  return hash.toString(16).padStart(8, '0');
}

async function sha256(input: string): Promise<string | null> {
  if (typeof crypto === 'undefined' || !crypto.subtle) return null;
  try {
    const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(input));
    return Array.from(new Uint8Array(digest))
      .map((byte) => byte.toString(16).padStart(2, '0'))
      .join('');
  } catch {
    return null;
  }
}

export interface TtsCacheKeyInput {
  text: string;
  voice: string;
  /** 服务端下发的合成身份（版本 + 供应商），见 syncSynthesisIdentity */
  identity: string;
}

/**
 * 缓存 key = 合成身份 + 音色 + 文本 的哈希，与服务端
 * lib/providers/tts/cache.ts 的 key 组成对齐。
 *
 * 不含 languageCode：服务端的语言由音色定义推导（resolveVoice 取
 * definition.languageCode），音色进了 key 就等价于语言进了 key。
 */
export async function buildTtsCacheKey({
  text,
  voice,
  identity,
}: TtsCacheKeyInput): Promise<string> {
  const raw = `${identity} ${voice} ${text}`;
  const hashed = await sha256(raw);
  // 带上长度，降低兜底哈希的碰撞概率
  return hashed ?? `${fnv1a(raw)}-${raw.length}`;
}

/**
 * 合成身份的本地副本。
 *
 * 服务端是唯一权威（lib/providers/tts/cache.ts 的 SYNTHESIS_VERSION + 当前
 * TTS_PROVIDER），客户端不复制那个常量，只从每次响应的 X-TTS-Synthesis 头
 * 跟随。值一变说明服务端换了版本或供应商，本地存量音频全部作废——否则切
 * TTS_PROVIDER 后服务端会重新合成，浏览器里却还播着上一家的音频。
 */
const IDENTITY_STORAGE_KEY = 'alfagpt-tts-synthesis';
/** 还没跟服务端对上话时的占位，第一次响应回来就会被替换 */
const UNKNOWN_IDENTITY = 'unknown';

let identity: string | null = null;

export function getSynthesisIdentity(): string {
  if (identity !== null) return identity;

  try {
    identity = window.localStorage.getItem(IDENTITY_STORAGE_KEY) ?? UNKNOWN_IDENTITY;
  } catch {
    // 无痕模式 / 禁用存储：退化成每个会话重新学一次，不影响正确性
    identity = UNKNOWN_IDENTITY;
  }
  return identity;
}

/**
 * 用响应头里的身份校准本地缓存：不一致就整体清空，再记下新值。
 * 返回校准后的身份，调用方据此算出真正该写入的 key。
 */
export async function syncSynthesisIdentity(serverIdentity: string | null): Promise<string> {
  const current = getSynthesisIdentity();
  if (!serverIdentity || current === serverIdentity) return current;

  await clearTtsCache();
  identity = serverIdentity;
  try {
    window.localStorage.setItem(IDENTITY_STORAGE_KEY, serverIdentity);
  } catch {
    // 存不下就下个会话再学一次，行为仍然正确
  }
  return serverIdentity;
}

/** 清掉上个存储格式残留的音频；只跑一次，失败也不影响主流程 */
let stalePurge: Promise<void> | null = null;
function purgeStaleCaches(): Promise<void> {
  stalePurge ??= Promise.all(
    STALE_CACHE_NAMES.map((name) => caches.delete(name).catch(() => false)),
  ).then(() => undefined);
  return stalePurge;
}

export async function getCachedAudio(key: string): Promise<Blob | null> {
  const inMemory = memoryCache.get(key);
  if (inMemory) return inMemory;

  if (!isCacheStorageAvailable()) return null;

  void purgeStaleCaches();

  try {
    const cache = await caches.open(CACHE_NAME);
    const response = await cache.match(`${CACHE_KEY_ORIGIN}${key}`);
    if (!response) return null;
    const blob = await response.blob();
    rememberInMemory(key, blob);
    return blob;
  } catch {
    return null;
  }
}

export async function putCachedAudio(key: string, blob: Blob): Promise<void> {
  rememberInMemory(key, blob);

  if (!isCacheStorageAvailable()) return;

  try {
    const cache = await caches.open(CACHE_NAME);
    await cache.put(
      `${CACHE_KEY_ORIGIN}${key}`,
      new Response(blob, { headers: { 'Content-Type': blob.type || 'audio/wav' } }),
    );
  } catch {
    // 配额不足 / 无痕模式：内存缓存已经生效，静默降级即可
  }
}

export async function clearTtsCache(): Promise<void> {
  memoryCache.clear();
  if (!isCacheStorageAvailable()) return;
  try {
    await caches.delete(CACHE_NAME);
  } catch {
    // ignore
  }
}

function rememberInMemory(key: string, blob: Blob): void {
  if (memoryCache.has(key)) {
    memoryCache.delete(key);
  } else if (memoryCache.size >= MEMORY_CACHE_LIMIT) {
    // Map 迭代顺序即插入顺序，删最早的一条 —— 简易 LRU
    const oldestKey = memoryCache.keys().next().value;
    if (oldestKey !== undefined) memoryCache.delete(oldestKey);
  }
  memoryCache.set(key, blob);
}
